import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Download, Filter, BarChart3, TableIcon, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import CommonFilters from "@/components/reports/CommonFilters";
import ReportTable, { SummaryCards } from "@/components/reports/ReportTable";
import ReportChart from "@/components/reports/ReportChart";
import { useAuth } from "@/context/AuthContext";

function buildParams(values) {
  const p = {};
  Object.entries(values).forEach(([k, v]) => {
    if (v !== "" && v !== null && v !== undefined) p[k] = v;
  });
  if (!p.range_key) p.range_key = "ALL";
  return p;
}

export default function ReportViewer() {
  const { reportKey } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = (user?.roles || [user?.role]).includes("admin");
  const [values, setValues] = useState({});
  const [view, setView] = useState("table"); // table | chart
  const [downloading, setDownloading] = useState(false);

  const setValue = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const { data: catalog } = useQuery({
    queryKey: ["reports-catalog"],
    queryFn: async () => (await api.get("/reports/catalog")).data,
  });

  const meta = useMemo(() => {
    if (!catalog) return null;
    for (const cat of catalog.categories) {
      const r = cat.reports.find((x) => x.key === reportKey);
      if (r) return r;
    }
    return null;
  }, [catalog, reportKey]);

  // Apply the report's default date range once (e.g. Broker Meeting Activity → last 30 days).
  useEffect(() => {
    if (meta) setValues((s) => (s.range_key ? s : { ...s, range_key: meta.default_range || "ALL" }));
  }, [meta]);

  const params = buildParams(values);
  const { data, isFetching, isError, error } = useQuery({
    queryKey: ["report", reportKey, params],
    queryFn: async () => (await api.get(`/reports/${reportKey}`, { params })).data,
    enabled: !!reportKey,
    keepPreviousData: true,
    retry: (failureCount, err) => {
      const s = err?.response?.status;
      if (s >= 400 && s < 500) return false;
      return failureCount < 3;
    },
  });

  const hasChart = !!data?.chart;

  const doExport = async (format) => {
    setDownloading(true);
    try {
      const res = await api.get(`/reports/${reportKey}/export`, {
        params: { ...params, format },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${reportKey}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${format.toUpperCase()}`);
    } catch (e) {
      let msg = "Export failed";
      if (e?.response?.data instanceof Blob) {
        try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch { /* noop */ }
      } else {
        msg = e?.response?.data?.detail || msg;
      }
      toast.error(msg);
    } finally {
      setDownloading(false);
    }
  };

  const filtersPanel = (
    <CommonFilters filters={meta?.filters || []} values={values} setValue={setValue} />
  );

  if (isError && error?.response?.status === 403) {
    return (
      <div className="p-8" data-testid="report-forbidden">
        <Button variant="ghost" onClick={() => navigate("/reports")} className="mb-4"><ArrowLeft className="h-4 w-4 mr-1.5" /> Reports</Button>
        <div className="text-sm text-red-600">You do not have access to this report.</div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="report-viewer">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <button onClick={() => navigate("/reports")} className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1" data-testid="back-to-reports">
            <ArrowLeft className="h-3.5 w-3.5" /> Reports
          </button>
          <h1 className="font-display text-2xl lg:text-3xl font-bold text-slate-900 mt-1">{meta?.title || "Report"}</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">{meta?.description}</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Mobile filter trigger */}
          <div className="lg:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" data-testid="mobile-filter-trigger"><Filter className="h-4 w-4 mr-1.5" /> Filters</Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-80 overflow-y-auto">
                <SheetHeader><SheetTitle>Filters</SheetTitle></SheetHeader>
                <div className="mt-4">{filtersPanel}</div>
              </SheetContent>
            </Sheet>
          </div>
          {hasChart && (
            <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
              <button onClick={() => setView("table")} className={`px-3 py-2 text-sm flex items-center gap-1.5 ${view === "table" ? "bg-slate-900 text-white" : "bg-white text-slate-700"}`} data-testid="view-table">
                <TableIcon className="h-3.5 w-3.5" /> Table
              </button>
              <button onClick={() => setView("chart")} className={`px-3 py-2 text-sm flex items-center gap-1.5 ${view === "chart" ? "bg-slate-900 text-white" : "bg-white text-slate-700"}`} data-testid="view-chart">
                <BarChart3 className="h-3.5 w-3.5" /> Chart
              </button>
            </div>
          )}
          {isAdmin && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800" disabled={downloading} data-testid="export-button">
                {downloading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />} Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => doExport("xlsx")} data-testid="export-xlsx">Excel (.xlsx)</DropdownMenuItem>
              <DropdownMenuItem onClick={() => doExport("csv")} data-testid="export-csv">CSV (.csv)</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="mt-6 flex gap-6">
        {/* Desktop filter sidebar */}
        <aside className="hidden lg:block w-64 shrink-0">
          <div className="bg-white border border-slate-200 rounded-md p-4 sticky top-6">
            <div className="label-eyebrow !text-slate-500 mb-3 flex items-center gap-1.5"><Filter className="h-3.5 w-3.5" /> Filters</div>
            {filtersPanel}
          </div>
        </aside>

        {/* Results */}
        <div className="flex-1 min-w-0 space-y-5">
          {isFetching && (
            <div className="text-xs text-slate-400 flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Updating…</div>
          )}
          {data?.summary && <SummaryCards summary={data.summary} />}
          {hasChart && view === "chart" ? (
            <ReportChart chart={data.chart} />
          ) : (
            <ReportTable columns={data?.columns || []} rows={data?.rows || []} />
          )}
          <div className="text-xs text-slate-400">{(data?.rows || []).length} row(s)</div>
        </div>
      </div>
    </div>
  );
}
